import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BarChart2,
  CircleHelp,
  Database,
  FileText,
  Home,
  Layers,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  Settings,
  Settings2,
  Sparkles,
  Tag,
  User as UserIcon,
  Users,
} from 'lucide-react'
import { Divider, Menu as MuiMenu, MenuItem } from '@mui/material'
import V3ClientSelect from './V3ClientSelect'
import V3HeaderHelpMenu from './V3HeaderHelpMenu'
import { useV3ShellLayout } from './V3ShellLayoutContext'
import { trackEvent } from '../../services/AmplitudeService'
import { cn } from '../../lib/utils'
import retailOrbitLogo from '../../assets/retail-orbit-logo.svg'

function getUserDisplayName(user) {
  return user?.name || user?.first_name || user?.email || 'User'
}

function getUserInitials(user) {
  const first = user?.first_name?.trim()?.[0]
  const last = user?.last_name?.trim()?.[0]
  if (first && last) return `${first}${last}`.toUpperCase()

  const name = user?.name?.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  }

  if (first) return first.toUpperCase()
  if (user?.email) return user.email[0].toUpperCase()
  return 'U'
}

function isNavItemActive(pathname, item) {
  if (item?.exact) {
    return pathname === item?.to
  }

  if (Array.isArray(item?.matchPrefixes) && item.matchPrefixes.length) {
    return item.matchPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix))
  }

  return pathname === item?.to
}

function getNavItemIcon(label) {
  const norm = String(label || '').toLowerCase()
  if (norm.includes('dashboard')) return LayoutGrid
  if (norm.includes('performance')) return BarChart2
  if (norm.includes('reports')) return FileText
  if (norm.includes('meeting')) return Users
  if (norm.includes('data')) return Database
  return LayoutGrid
}

function getProfileMenuItemIcon(label) {
  const norm = String(label || '').toLowerCase()
  if (norm.includes('profile')) return UserIcon
  if (norm.includes('location')) return Home
  if (norm.includes('categor')) return Layers
  if (norm.includes('class')) return Tag
  if (norm.includes('pos')) return Settings2
  if (norm.includes('account')) return Settings2
  if (norm.includes('help')) return CircleHelp
  return UserIcon
}

const profileMenuItemSx = {
  gap: 1.25,
  mx: 0.5,
  borderRadius: '6px',
  py: 0.75,
  px: 1,
  fontSize: 14,
  fontWeight: 400,
  color: '#101828',
  '&:hover': {
    backgroundColor: '#F9FAFB',
  },
}

export default function V3ShellHeader({
  brandHref,
  navItems = [],
  currentUser,
  onLogout,
  showClientSelect = false,
  clientSelectPlaceholder,
  clientSelectUnselectPath,
  askIndieHref,
  onOpenAskIndie,
  profileHref,
  accountHref,
  profileMenuItems = [],
  showInlineNav = true,
  showNavToggle = false,
  onToggleNav,
  settingsMenuItems = [],
}) {
  const location = useLocation()
  const { leftNavWidth } = useV3ShellLayout()
  const displayName = getUserDisplayName(currentUser)
  const userInitials = getUserInitials(currentUser)
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null)
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null)
  const settingsMenuOpen = Boolean(settingsAnchorEl)
  const profileMenuOpen = Boolean(profileAnchorEl)
  const visibleSettingsItems = settingsMenuItems.filter((item) => !item?.hidden)

  const handleNavItemClick = React.useCallback((item) => {
    trackEvent('V3 Navigation Event', {
      action: 'Primary Nav Clicked',
      clientCode: 'global',
      currentPath: location.pathname,
      navKey: item?.key || '',
      navLabel: item?.label || '',
      targetPath: item?.to || '',
    })
  }, [location.pathname])

  const handleAskIndieButtonClick = React.useCallback(() => {
    trackEvent('V3 Ask Indie Event', {
      action: 'Open Clicked',
      clientCode: 'global',
      currentPath: location.pathname,
      source: 'shell_button',
    })
    onOpenAskIndie?.()
  }, [location.pathname, onOpenAskIndie])

  const handleAskIndieLinkClick = React.useCallback(() => {
    trackEvent('V3 Ask Indie Event', {
      action: 'Open Clicked',
      clientCode: 'global',
      currentPath: location.pathname,
      source: 'shell_link',
      targetPath: askIndieHref || '',
    })
  }, [askIndieHref, location.pathname])

  const shellContentContainerStyle = React.useMemo(() => (
    leftNavWidth > 0
      ? {
        marginLeft: `${leftNavWidth + 24}px`,
        marginRight: '24px',
      }
      : {
        marginLeft: '2.5vw',
        marginRight: '2.5vw',
      }
  ), [leftNavWidth])

  const openSettingsMenu = (event) => {
    setSettingsAnchorEl(event.currentTarget)
  }

  const closeSettingsMenu = () => {
    setSettingsAnchorEl(null)
  }

  const openProfileMenu = (event) => {
    setProfileAnchorEl(event.currentTarget)
  }

  const closeProfileMenu = () => {
    setProfileAnchorEl(null)
  }

  const handleLogout = () => {
    closeProfileMenu()
    onLogout?.()
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-gray-100 bg-white">
      <div className="flex min-h-16 sm:min-h-20 flex-col justify-center" style={shellContentContainerStyle}>
        <div className="flex flex-nowrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 sm:gap-3">
            {showNavToggle && (
              <button
                type="button"
                aria-label="Open navigation menu"
                onClick={onToggleNav}
                className="inline-flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-[12px] border border-[#EAECF0] bg-white text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
              >
                <MenuIcon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </button>
            )}

            <Link
              to={brandHref}
              aria-label="Retail Orbit"
              className="inline-flex shrink-0 items-center text-inherit no-underline"
            >
              <img
                src={retailOrbitLogo}
                alt=""
                aria-hidden="true"
                className="h-8 w-auto sm:h-10"
              />
            </Link>

            {visibleSettingsItems.length > 0 && (
              <>
                <button
                  type="button"
                  aria-label="Settings"
                  aria-controls={settingsMenuOpen ? 'v3-settings-menu' : undefined}
                  aria-expanded={settingsMenuOpen ? 'true' : undefined}
                  aria-haspopup="menu"
                  onClick={openSettingsMenu}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#EAECF0] bg-white text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                >
                  <Settings className="h-4 w-4" strokeWidth={1.6} />
                </button>

                <MuiMenu
                  id="v3-settings-menu"
                  anchorEl={settingsAnchorEl}
                  open={settingsMenuOpen}
                  onClose={closeSettingsMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 220,
                        border: '1px solid #EAECF0',
                        borderRadius: '16px',
                        boxShadow: '0 16px 36px rgba(16,24,40,0.12)',
                        overflow: 'hidden',
                      },
                    },
                  }}
                >
                  {visibleSettingsItems.map((item) => (
                    <MenuItem
                      key={item.key || item.to || item.label}
                      component={Link}
                      to={item.to}
                      onClick={closeSettingsMenu}
                      sx={{ py: 1.25, fontSize: 14, fontWeight: 500, color: '#344054' }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </MuiMenu>
              </>
            )}

            {showClientSelect && (
              <V3ClientSelect
                className="hidden md:block w-[180px] sm:w-[210px] shrink-0"
                placeholder={clientSelectPlaceholder}
                unselectNavigatePath={clientSelectUnselectPath}
              />
            )}

            {showInlineNav && navItems.length > 0 && (
              <nav className="ml-1 flex shrink-0 items-center gap-1">
                {navItems.map((item) => {
                  const isActive = !item.disabled && isNavItemActive(location.pathname, item)
                  const NavIcon = getNavItemIcon(item.label)
                  const navClasses = cn(
                    'inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-2.5 sm:px-3 text-xs sm:text-sm font-medium no-underline transition-colors shrink-0 cursor-pointer',
                    item.disabled
                      ? 'cursor-not-allowed bg-gray-50 text-gray-300'
                      : isActive
                        ? 'bg-gray-100 text-gray-900 font-semibold'
                        : 'bg-white text-gray-700 hover:bg-gray-100/80',
                  )

                  if (item.disabled) {
                    return (
                      <span key={item.key || item.to || item.label} className={navClasses} aria-disabled="true">
                        <NavIcon className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={1.8} />
                        {item.label}
                      </span>
                    )
                  }

                  return (
                    <Link
                      key={item.key || item.to || item.label}
                      to={item.to}
                      onClick={() => handleNavItemClick(item)}
                      className={navClasses}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <NavIcon className="h-4 w-4 shrink-0 text-gray-600" strokeWidth={1.8} />
                      {item.label}
                    </Link>
                  )
                })}
              </nav>
            )}
          </div>

          <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-3 py-3">
            {onOpenAskIndie ? (
              <button
                type="button"
                onClick={handleAskIndieButtonClick}
                className="hidden sm:inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-[#7F56D9] px-4 text-sm font-medium text-white shadow-xs transition-colors hover:bg-[#6941C6] cursor-pointer"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </button>
            ) : askIndieHref ? (
              <Link
                to={askIndieHref}
                onClick={handleAskIndieLinkClick}
                className="hidden sm:inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-[#7F56D9] px-4 text-sm font-medium text-white no-underline shadow-xs transition-colors hover:bg-[#6941C6]"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </Link>
            ) : (
              <button
                type="button"
                className="hidden sm:inline-flex h-9 shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-full bg-[#7F56D9] px-4 text-sm font-medium text-white opacity-70 shadow-xs"
                disabled
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </button>
            )}

            <button
              type="button"
              aria-label="Open profile menu"
              aria-controls={profileMenuOpen ? 'v3-profile-menu' : undefined}
              aria-expanded={profileMenuOpen ? 'true' : undefined}
              aria-haspopup="menu"
              onClick={openProfileMenu}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white transition-colors hover:bg-[#F9FAFB] cursor-pointer"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEEDFE] text-xs font-bold text-[#534AB7]">
                {userInitials}
              </span>
            </button>

            <MuiMenu
              id="v3-profile-menu"
              anchorEl={profileAnchorEl}
              open={profileMenuOpen}
              onClose={closeProfileMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 224,
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <div className="flex items-center gap-2.5 px-3.5 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEEDFE] text-xs font-bold text-[#534AB7]">
                  {userInitials}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold text-[#101828]">{displayName}</span>
                  {currentUser?.email && <span className="mt-0.5 block truncate text-xs text-[#667085]">{currentUser.email}</span>}
                </span>
              </div>
              <Divider />
              {profileHref && (
                <MenuItem component={Link} to={profileHref} onClick={closeProfileMenu} sx={profileMenuItemSx}>
                  <UserIcon className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                  Profile
                </MenuItem>
              )}
              {profileMenuItems.map((item) => {
                const ItemIcon = getProfileMenuItemIcon(item.label)
                return (
                  <MenuItem
                    key={item.key || item.to || item.label}
                    component={Link}
                    to={item.to}
                    onClick={closeProfileMenu}
                    sx={profileMenuItemSx}
                  >
                    <ItemIcon className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                    {item.label}
                  </MenuItem>
                )
              })}
              {profileMenuItems.length === 0 && (
                <>
                  <MenuItem component={Link} to="/v3/locations" onClick={closeProfileMenu} sx={profileMenuItemSx}>
                    <Home className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                    Locations
                  </MenuItem>
                  <MenuItem component={Link} to="/v3/category" onClick={closeProfileMenu} sx={profileMenuItemSx}>
                    <Layers className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                    Category
                  </MenuItem>
                  <MenuItem component={Link} to="/v3/classes" onClick={closeProfileMenu} sx={profileMenuItemSx}>
                    <Tag className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                    Classes
                  </MenuItem>
                </>
              )}
              <Divider />
              {accountHref && (
                <MenuItem component={Link} to={accountHref} onClick={closeProfileMenu} sx={profileMenuItemSx}>
                  <Settings2 className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                  Account
                </MenuItem>
              )}
              <MenuItem onClick={closeProfileMenu} sx={profileMenuItemSx}>
                <CircleHelp className="h-4 w-4 text-[#667085]" strokeWidth={1.5} />
                Help
              </MenuItem>
              <Divider />
              {onLogout && (
                <MenuItem
                  onClick={handleLogout}
                  sx={{ ...profileMenuItemSx, color: '#F04438', '&:hover': { backgroundColor: '#FEF3F2' } }}
                >
                  <LogOut className="h-4 w-4 text-[#F04438]" strokeWidth={1.5} />
                  Logout
                </MenuItem>
              )}
            </MuiMenu>

            <V3HeaderHelpMenu />
          </div>
        </div>
      </div>
    </header>
  )
}


